import type { Graph } from '@/schema/estate';
import { getItem } from './itemService';
import { addItemTag, itemTagNames, itemTagRowsNamed, removeItemTag } from './tagService';

export const FLAG_NAMES = [
  'review',
  'measure',
  'research',
  'appraisal',
  'provenance',
  'photo_needed',
  'condition_review',
  'high_priority',
] as const;

export type FlagName = (typeof FLAG_NAMES)[number];

const FLAG_PREFIX = 'flag:';
const tagName = (flag: string): string => `${FLAG_PREFIX}${flag}`;

/** The flag a tag name stands for, or undefined for a plain tag. */
export const flagOf = (name: string): string | undefined =>
  name.startsWith(FLAG_PREFIX) ? name.slice(FLAG_PREFIX.length) : undefined;

/** The item's tags that aren't flags, by name, each once. */
export const plainTagNames = (g: Graph, itemId: string): string[] =>
  [...new Set(itemTagNames(g, itemId).filter((name) => flagOf(name) === undefined))].sort((a, b) =>
    a.localeCompare(b),
  );

export function hasFlag(g: Graph, itemId: string, flag: string): boolean {
  const item = getItem(g, itemId);
  if (!item?.estate_id) return false;
  return itemTagRowsNamed(g, { itemId, estateId: item.estate_id, name: tagName(flag) }).length > 0;
}

export async function toggleFlag(
  g: Graph,
  opts: { itemId: string; estateId: string; groupId: string; author: string; flag: string },
): Promise<boolean> {
  const tag = { itemId: opts.itemId, estateId: opts.estateId, name: tagName(opts.flag) };
  if (itemTagRowsNamed(g, tag).length > 0) {
    await removeItemTag(g, tag);
    return false;
  }
  await addItemTag(g, { ...tag, groupId: opts.groupId });
  return true;
}
